import { useForm } from "antd/lib/form/Form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../shared/types/common";
import { type CSSProperties, useCallback, useEffect, useState, useMemo } from "react";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import { Button, Col, Drawer, Modal, Row, Space, Switch, Tag } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  UserOutlined,
  FlagOutlined,
  CheckOutlined,
  ProjectOutlined,
  CloseOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { BiFilterAlt } from "react-icons/bi";
import ContentHeaderComponent from "../../shared/components/content-header/content-header.component";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import {
  approveProject,
  deleteProjectById,
  getProjectList,
  getStaffAssignments,
} from "../../core/store/reducers/project.slice";
import CreateEditProjectComponent from "./components/create-edit-project.component";
import {
  accessAllowed,
  decryptCTC,
  formatCost,
  formatCost1,
  showNotification,
} from "../../shared/utils/utils";
import { Client } from "../../core/api-client";
import {
  API_GET_ALL_ACCOUNTS_LIST,
  API_GET_ALL_DEPARTMENT_LIST,
  API_GET_ALL_STAFF_LIST,
  API_GET_ACTIVE_CONTRACT_EXPIRY_LIST,
} from "../../shared/routes/private.routes";
import moment from "moment";
import Papa from "papaparse";
import ActivityLogComponent from "../../shared/components/activity-log/activity-log.component";
import StaffAssignmentComponent from "./components/staff-assignment.component";
import MilestonesDrawerComponent from "./components/milestones-drawer.component";
import { requestGetByModuleCommon } from "../../core/store/reducers/master.slice";
import { allPermissions } from "../../shared/constants/constants";
import ProjectBulkUploadComponent from "./components/project-bulk-upload.component";
import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";
import { isFilterValueActive } from "../../shared/utils/active-filters";

const PROJECT_FILTER_LABELS: Record<string, string> = {
  sowNumber: "SOW",
  poNumber: "PO Number",
  projectName: "Project name",
  projectType: "Project type",
  status: "Status",
  projectManagerId: "Project manager",
  accountManagerId: "Account manager",
  employeeId: "Assigned employee",
  accountId: "Client",
};

function projectFilterValueText(
  key: string,
  value: any,
  ctx: {
    projectTypes: any[];
    staffDropdownList: any[];
    accountList: any[];
  }
): string {
  const { projectTypes, staffDropdownList, accountList } = ctx;
  switch (key) {
    case "sowNumber":
    case "poNumber":
    case "projectName":
      return String(value).trim();
    case "projectType": {
      const ids = Array.isArray(value) ? value : [value];
      return ids
        .map(
          (id: string) => projectTypes.find((t) => t.id === id)?.name ?? id
        )
        .join(", ");
    }
    case "status":
      return Array.isArray(value) ? value.join(", ") : String(value);
    case "projectManagerId":
    case "accountManagerId":
    case "employeeId": {
      const ids = Array.isArray(value) ? value : [value];
      return ids
        .map(
          (id: string) =>
            staffDropdownList.find(
              (s) => s.staffId === id || s.employeeId === id
            )?.staffName ?? id
        )
        .join(", ");
    }
    case "accountId": {
      const ids = Array.isArray(value) ? value : [value];
      return ids
        .map(
          (id: string) =>
            accountList.find((a) => a.accountId === id)?.accountName ?? id
        )
        .join(", ");
    }
    default:
      return Array.isArray(value) ? value.join(", ") : String(value);
  }
}

const PROJECT_FILTER_CLEAR: Record<string, any> = {
  sowNumber: undefined,
  poNumber: undefined,
  projectName: undefined,
  projectType: [],
  status: [],
  projectManagerId: [],
  accountManagerId: [],
  employeeId: [],
  accountId: [],
};

const APPROVE_BUTTON_STYLE: CSSProperties = {
  background: "var(--success)",
  borderColor: "var(--success)",
  color: "#ffffff",
};
const ManageProjectPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = useForm();
  const { loadingProject, projectList, pagination } = useSelector(
    (state: RootState) => state.project
  );
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  // Delete confirmation modal
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [projectIdToDelete, setProjectIdToDelete] = useState("");
  const { screenType } = useSelector((state: RootState) => state.settings);
  // List local state
  const [projectListData, setProjectListData] = useState([] as any);

  // Flags
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null as any);
  const [filterVisible, setFilterVisible] = useState(false);
  const [staffDropdownList, setStaffList] = useState<any[]>([]);
  const [departmentList, setDepartmentList] = useState<any[]>([]);
  const [accountList, setAccountList] = useState<any[]>([]);
  const [openLogs, setOpenLogs] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [openStaffAssignment, setOpenStaffAssignment] = useState(false);
  const [openMilestones, setOpenMilestones] = useState(false);
  const [entityList, setEntityList] = useState<any[]>([]);
  const [projectTypes, setProjectTypes] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  // Actions and selections
  const [sorter, setSorter] = useState({ sort_by: "", sort_type: "" });
  const [filters, setFilters] = useState({} as any);
  const [projectsWithCost, setProjectsWithCost] = useState<any[]>([]);
  const [enableCostCalculation, setEnableCostCalculation] = useState(false);
  const [loadingCostCalculation, setLoadingCostCalculation] = useState(false);

  useEffect(() => {
    if (projectList?.length) {
      fetchProjectWithCost();
    }
  }, [projectList, enableCostCalculation]);

  const fetchProjectWithCost = async () => {
    setLoadingCostCalculation(true);
    // If cost calculation is disabled, set totalSpend to 0 for all projects
    if (!enableCostCalculation) {
      const updatedProjects = projectList.map((project: any) => ({
        ...project,
        totalSpend: 0,
      }));
      setProjectsWithCost(updatedProjects);
      setLoadingCostCalculation(false);
      return;
    }

    // Perform cost calculation when enabled
    const updatedProjects = await Promise.all(
      projectList.map(async (project: any) => {
        let projectTotal = 0;

        if (project.staffCostDetails?.length) {
          for (const detail of project.staffCostDetails) {
            let staffCtc = detail?.staffSalary?.ctc;

            if (detail?.staffSalary?.encrypted_ctc) {
              try {
                staffCtc = await decryptCTC(
                  moment(detail?.staffSalary?.updated_date).format(
                    "YYYY-MM-DD"
                  ),
                  detail?.staffSalary?.encrypted_ctc,
                  detail?.staffSalary?.iv,
                  detail?.staffSalary?.salt
                );
              } catch (err) {
                console.error("decryptCTC failed:", err, detail);
                staffCtc = 0; // fallback
              }
            }

            if (detail.percentAllocation) {
              const staffCost =
                Number(staffCtc || 0) / 12 *
                Number(detail.inrMultiplier || 1) *
                ((Number(detail.percentAllocation) || 1) / 100);

              const totalSpendInProjectCurrency =
                staffCost / Number(detail.projectCurrencyRate || 1);

              projectTotal += totalSpendInProjectCurrency;
            }
          }
        }

        return {
          ...project,
          totalSpend: projectTotal,
        };
      })
    );

    setProjectsWithCost(updatedProjects);
    setLoadingCostCalculation(false);
  };

  useEffect(() => {
    loadList(1);
  }, [filters]);
  const loadList = (page: any = null) => {
    setProjectsWithCost([]);
    const data = {
      page: page ? page : pagination?.page ? pagination?.page : 1,
      limit: pagination?.limit || TABLE_ROWS_COUNT,
      ...sorter,
    };
    const filter = {} as any;
    Object.keys(filters)?.map((val: any) => {
      if (filters[val]) {
        filter[val] =
          val === "employeeId" && !Array.isArray(filters[val])
            ? [filters[val]]
            : filters[val];
      }
    });
    dispatch(getProjectList({ params: data, body: filter }));
  };
  const createEntityListner = () => {
    setCreateEditOpen(true);
    setSelectedEntity(null);
  };
  const bulkImportEntityListner = () => {
    setBulkImportOpen(true);
    // setSelectedEntity(null);
  };
  const editEntityListner = async (entity: any) => {
    setCreateEditOpen(true);
    const response = await dispatch(
      getStaffAssignments(entity?.projectId),
    ).unwrap();
    setSelectedEntity({...entity, staffAssignments: response?.data?.data});
  };
  useEffect(() => {
    setProjectListData(
      projectsWithCost.map((obj: any, index: number) => {
        return { key: index, ...obj };
      })
    );
  }, [projectsWithCost]);
  const onFilterApply = (values: any) => {
    setFilters(values);
  };

  const activeFilterEntries = useMemo(() => {
    return Object.keys(filters)
      .filter((k) => isFilterValueActive(filters[k]))
      .map((key) => ({
        key,
        fieldLabel: PROJECT_FILTER_LABELS[key] || key,
        valueText: projectFilterValueText(key, filters[key], {
          projectTypes,
          staffDropdownList,
          accountList,
        }),
      }));
  }, [filters, projectTypes, staffDropdownList, accountList]);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    form.resetFields();
  }, [form]);

  const removeFilter = useCallback(
    (key: string) => {
      setFilters((prev: any) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      const cleared =
        key in PROJECT_FILTER_CLEAR ? PROJECT_FILTER_CLEAR[key] : undefined;
      form.setFieldsValue({ [key]: cleared });
    },
    [form]
  );

  const onPageChange = (pageNumber: number) => {
    loadList(pageNumber);
  };

  const fetchEntityList = async () => {
    try {
      const response = await dispatch(
        requestGetByModuleCommon({ module: "master_entity" })
      ).unwrap();
      if (response?.success) setEntityList(response?.data.items);
    } catch (err) {
      console.error("requestGetByModuleCommon failed:", err);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await dispatch(
        requestGetByModuleCommon({ module: "master_currency" })
      ).unwrap();
      // console.log(response.data);
      if (response?.success) {
        setCurrencies(response?.data.items);
      }
    } catch (error) {}
  };

  const fetchProjectTypes = async () => {
    try {
      const response = await dispatch(
        requestGetByModuleCommon({ module: "master_project_type" })
      ).unwrap();
      // console.log(response.data);
      if (response?.success) {
        setProjectTypes(response?.data.items);
      }
    } catch (error) {}
  };
  const hasAnyProjectAccess =
    accessAllowed(myPermissions, allPermissions.UPDATE_PROJECT) ||
    accessAllowed(myPermissions, allPermissions.DELETE_PROJECT) ||
    accessAllowed(myPermissions, allPermissions.PROJECT_ACTIVITY_LOGS) ||
    accessAllowed(myPermissions, allPermissions.PROJECT_STAFF_ASSIGNMENT) ||
    accessAllowed(myPermissions, allPermissions.VIEW_PROJECT) ||
    accessAllowed(myPermissions, allPermissions.APPROVE_PROJECT);
  
  const columns = useMemo(() => [
    {
      title: "SOW/Quotation (Internal)",
      dataIndex: "sowNumber",
      sorter: false,
      key: "sowNumber",
    },
    {
      title: "PO Number (Client Provided)",
      dataIndex: "poNumber",
      sorter: false,
      key: "poNumber",
    },
    {
      title: "Client",
      dataIndex: "accountName",
      sorter: false,
      key: "accountName",
    },
    {
      title: "Project",
      dataIndex: "l2ProjectName",
      sorter: false,
      key: "l2ProjectName",
    },
    {
      title: "Project Type",
      dataIndex: "projectTypeName",
      sorter: false,
      key: "projectTypeName",
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      sorter: false,
      key: "startDate",
      render: (startDate: string) =>
        startDate ? moment(startDate).format("DD-MMM-YYYY ") : "",
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      sorter: false,
      key: "endDate",
      render: (endDate: string) =>
        endDate ? moment(endDate).format("DD-MMM-YYYY ") : "",
    },
    {
      title: "PO Value",
      dataIndex: "poValue",
      sorter: false,
      key: "poValue",
      render: (poValue: number, record: any) =>
        poValue ? formatCost1(poValue, record.poCurrencyName) : "",
    },
    ...(enableCostCalculation
      ? [
          {
            title: "Total Cost",
            dataIndex: "totalSpend",
            sorter: false,
            key: "totalSpend",
            render: (totalSpend: number, record: any) =>
              totalSpend ? formatCost1(totalSpend, record.poCurrencyName) : "",
          },
        ]
      : []),
    {
      title: "Status",
      dataIndex: "status",
      sorter: false,
      key: "status",
      render: (status: string) => {
        const color = status === "ACTIVE" ? "success" : status === "DRAFT" ? "processing" : "red";
        return status ? <Tag color={color}>{status}</Tag> : "-";
      },
    },
    // {
    //   title: "Project Code",
    //   dataIndex: "projectCode",
    //   sorter: false,
    //   key: "projectCode",
    // },
    {
      title: "Project Manager",
      dataIndex: "projectManagerName",
      sorter: false,
      key: "projectManagerName",
    },
    {
      title: "Account Manager",
      dataIndex: "accountManagerName",
      sorter: false,
      key: "accountManagerName",
    },
    // {
    //   title: "Department Name",
    //   dataIndex: "departmentName",
    //   sorter: false,
    //   key: "departmentName",
    // },
    ...(hasAnyProjectAccess
      ? [
          {
            title: "Actions",
            key: "actions",
            fixed: "right",
            width: '240px',
            align: 'center',
            render: (text: any, record: any) => {
              const actionSlotStyle: CSSProperties = {
                display: "flex",
                justifyContent: "center",
                width: 32,
              };
              const approveSlotStyle: CSSProperties = {
                ...actionSlotStyle,
                width: 40,
              };
              const canUpdateProject = accessAllowed(
                myPermissions,
                allPermissions.UPDATE_PROJECT
              );
              const canViewProjectLogs = accessAllowed(
                myPermissions,
                allPermissions.PROJECT_ACTIVITY_LOGS
              );
              const canAssignProjectStaff =
                record?.status === "ACTIVE" &&
                accessAllowed(
                  myPermissions,
                  allPermissions.PROJECT_STAFF_ASSIGNMENT
                );
              const canViewProjectMilestones =
                record?.status === "ACTIVE" &&
                accessAllowed(
                  myPermissions,
                  allPermissions.VIEW_PROJECT
                );
              const canApproveProject =
                record?.status === "DRAFT" &&
                accessAllowed(myPermissions, allPermissions.APPROVE_PROJECT);

              return (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 32px) 40px",
                    alignItems: "center",
                    justifyContent: "center",
                    columnGap: 8,
                    width: "100%",
                  }}
                >
                  <div style={actionSlotStyle}>
                    {canUpdateProject && (
                      <Button
                        type="link"
                        onClick={() => editEntityListner(record)}
                        icon={<EditOutlined />}
                        title="Edit"
                      />
                    )}
                  </div>
                  {/* {accessAllowed(
                    myPermissions,
                    allPermissions.DELETE_PROJECT
                  ) && (
                    <Button
                      type="link"
                      onClick={() => handleDeleteModal(record.projectId)}
                      icon={<DeleteOutlined style={{color:"#FF0000"}}  />}
                    />
                  )} */}
                  <div style={actionSlotStyle}>
                    {canViewProjectLogs && (
                      <Button
                        type="link"
                        onClick={() => handleOpenLogs(record)}
                        icon={<EyeOutlined />}
                        title="Activity log"
                      />
                    )}
                  </div>
                  <div style={actionSlotStyle}>
                    {canAssignProjectStaff && (
                      <Button
                        type="link"
                        onClick={() => handleStaffAssignment(record)}
                        icon={<UserOutlined />}
                        title="Resource assignment"
                      />
                    )}
                  </div>
                  <div style={actionSlotStyle}>
                    {canViewProjectMilestones && (
                      <Button
                        type="link"
                        onClick={() => handleMilestones(record)}
                        icon={<FlagOutlined />}
                        title="Milestones"
                      />
                    )}
                  </div>
                  <div style={approveSlotStyle}>
                    {canApproveProject && (
                      <Button
                        shape="circle"
                        size="small"
                        onClick={() => handleApproveProject(record)}
                        icon={<CheckOutlined />}
                        title="Approve"
                        style={APPROVE_BUTTON_STYLE}
                      />
                    )}
                  </div>
                </div>
              );
            },
          },
        ]
      : []),
  ], [enableCostCalculation, myPermissions, hasAnyProjectAccess]);
  const handleApproveProject = (record: any) => {
    dispatch(approveProject(record.projectId)).then((val: any) => {
      if (val?.payload?.data?.statusCode === 200) {
        showNotification("Project Approved Successfully", "success");
        loadList();
      } else if (val?.payload?.data?.message) {
        showNotification(val.payload.data.message, "warning");
      }
    });
  };

  const handleDelete = (projectId: string) => {
    dispatch(deleteProjectById(projectId)).then((val: any) => {
      if (val?.payload?.data) {
        showNotification("Project Deleted Successfully", "success");
        loadList();
        setDeleteModalVisible(false); // Close the modal after successful deletion
      }
    });
  };

  const handleDeleteModal = (projectId: string) => {
    setDeleteModalVisible(true);
    setProjectIdToDelete(projectId);
  };

  const onDeleteConfirm = () => {
    handleDelete(projectIdToDelete);
  };

  const onDeleteCancel = () => {
    setDeleteModalVisible(false);
    setProjectIdToDelete("");
  };

  const handleStaffAssignment = (record: any) => {
    setOpenStaffAssignment(true);
    setSelectedProjectId(record?.projectId);
    setSelectedEntity(record);
  };

  const handleMilestones = (record: any) => {
    setOpenMilestones(true);
    setSelectedEntity(record);
  };

  const onCloseLogs = () => {
    setOpenLogs(false);
    setSelectedProjectId("");
  };

  const onCloseStaffAssignment = () => {
    setOpenStaffAssignment(false);
    setSelectedProjectId("");
    setSelectedEntity(null);
    loadList();
  };

  const onCloseMilestones = () => {
    setOpenMilestones(false);
    setSelectedEntity(null);
  };

  const handleOpenLogs = (record: any) => {
    setOpenLogs(true);
    setSelectedProjectId(record?.projectId);
    setSelectedEntity(record);
  };

  const onChangeHandler = (_pagination: any, _filters: any, _sorter: any) => {
    if (_sorter.order === undefined) {
      setSorter({ sort_by: "", sort_type: "" });
    } else {
      let order = "";
      let columnKey = "";
      if (_sorter.order === "ascend") order = "asc";
      else order = "desc";

      if (_sorter.columnKey === "enabled") {
        columnKey = "status";
      } else if (_sorter.columnKey === "displayName") {
        columnKey = "name";
      } else {
        columnKey = _sorter.columnKey;
      }
      setSorter({ sort_by: columnKey, sort_type: order });
    }
  };
  const createEditSubmit = () => {
    setCreateEditOpen(false);
    loadList();
  };
  const onValuesChange = (value: any, _allValues: any) => {};

  useEffect(() => {
    fetchStaffList();
    fetchDepartmentList();
    fetchAccountList();
    fetchEntityList();
    fetchProjectTypes();
    fetchCurrencies();
  }, []);

  const fetchStaffList = async () => {
    const response = await Client.get(API_GET_ALL_STAFF_LIST);
    if (response?.data?.data) {
      setStaffList(response?.data?.data);
    } else {
      setStaffList([]);
    }
  };

  const fetchDepartmentList = async () => {
    const response = await Client.get(API_GET_ALL_DEPARTMENT_LIST);
    if (response?.data?.data) {
      setDepartmentList(response?.data?.data);
    } else {
      setDepartmentList([]);
    }
  };
  const fetchAccountList = async () => {
    const response = await Client.get(API_GET_ALL_ACCOUNTS_LIST);
    if (response?.data?.data) {
      setAccountList(response?.data?.data);
    } else {
      setAccountList([]);
    }
  };

  const exportActiveContractExpiryList = async () => {
    try {
      const filter = {} as any;
      Object.keys(filters)?.forEach((val: any) => {
        if (val === "status") {
          return;
        }
        if (filters[val]) {
          filter[val] =
            val === "employeeId" && !Array.isArray(filters[val])
              ? [filters[val]]
              : filters[val];
        }
      });

      const result = await Client.post(API_GET_ACTIVE_CONTRACT_EXPIRY_LIST, filter);
      const rows = result?.data?.data || [];
      if (!rows.length) {
        showNotification("No active contracts with expiry date found", "warning");
        return;
      }

      const rowData = rows.map((item: any) => ({
        Entity: item?.entityName || "",
        Client: item?.accountName || "",
        "SOW/Quotation Number(Unique)": item?.sowNumber || "",
        "PO Number(Unique)": item?.poNumber || "",
        Project: item?.l2ProjectName || item?.projectName || "",
        "Project Type": item?.projectTypeName || "",
        "Start Date": item?.startDate ? moment(item.startDate).format("DD-MM-YYYY") : "",
        "Expiry Date": item?.endDate ? moment(item.endDate).format("DD-MM-YYYY") : "",
        "Days Remaining": Number.isFinite(Number(item?.daysRemaining))
          ? Number(item.daysRemaining)
          : "",
        "Expired": item?.isExpired ? "Yes" : "No",
        Status: item?.status || "",
        "Project Manager": item?.projectManagerName || "",
        "Project Manager Email": item?.projectManagerEmail || "",
        "Account Manager": item?.accountManagerName || "",
        "Account Manager Email": item?.accountManagerEmail || "",
        "PO Currency": item?.poCurrencyName || "",
        "PO Value": item?.poValue || "",
        "Margin(%)": item?.margin || "",
      }));

      const csv = Papa.unparse(rowData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "active_contracts_with_expiry_date.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      showNotification("Failed to export active contract expiry list", "error");
    }
  };

  const staffDropdownOptions = staffDropdownList.map((staff) => ({
    value: staff.staffId,
    label: staff.employeeId
      ? `${staff.staffName} (${staff.employeeId})`
      : staff.staffName,
  }));

  const employeeDropdownOptions = staffDropdownList.map((staff) => ({
    value: staff.employeeId,
    label: staff.employeeId
      ? `${staff.staffName} (${staff.employeeId})`
      : staff.staffName,
  }));

  const departmentDropdownOptions = departmentList.map((department) => ({
    value: department?.departmentId,
    label: department?.departmentName,
  }));

  const accountDropdownOptions = accountList.map((account) => ({
    value: account?.accountId,
    label: account?.accountName + " - " + account?.entityName,
  }));

  const formProps = {
    form: form,
    name: "manage-project-filters",
    enableEnterSubmit: true,
    formElements: [
      {
        name: "sowNumber",
        label: "",
        placeholder: "SOW Number Search",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
      {
        name: "poNumber",
        label: "",
        placeholder: "PO Number Search",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
      
      {
        name: "projectName",
        label: "",
        placeholder: "Project Name Search",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
      {
        name: "projectType",
        label: "",
        placeholder: "Project Type",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: projectTypes?.map((type) => ({
          value: type?.id,
          label: type?.name,
        })),
      },
      {
        name: "status",
        label: "",
        placeholder: "Status",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: [
          { value: "DRAFT", label: "DRAFT" },
          { value: "ACTIVE", label: "ACTIVE" },
          { value: "INACTIVE", label: "INACTIVE" },
        ],
      },
      // {
      //   name: "departmentId",
      //   label: "",
      //   placeholder: "Department",
      //   type: "Select",
      //   colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      //   showSearch: true,
      //   allowClear: true,
      //   mode: "multiple",
      //   options: departmentDropdownOptions,
      // },
      {
        name: "projectManagerId",
        label: "",
        placeholder: "Project Manager",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: staffDropdownOptions,
      },
      {
        name: "accountManagerId",
        label: "",
        placeholder: "Account Manager",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: staffDropdownOptions,
      },
      {
        name: "employeeId",
        label: "",
        placeholder: "Assigned Employee",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: employeeDropdownOptions,
      },
      {
        name: "accountId",
        label: "",
        placeholder: "Client",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: accountDropdownOptions,
      },
    ],
    initialValues: {
      name: "",
    },
    extraButtons: [
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
        onClick: clearAllFilters,
      },
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Apply",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFilterApply,
    onFinishFailed: null,
    onValuesChange: onValuesChange,
  };
  const headerButtons = (
    <Space size="middle" className="app-page-actions">
      {accessAllowed(myPermissions, allPermissions.PROJECT_BULK_UPLOAD) && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={bulkImportEntityListner}
        >
          <span className="font-size-14">Project Bulk import</span>
        </Button>
      )}
      {accessAllowed(myPermissions, allPermissions.CREATE_PROJECT) && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={createEntityListner}
        >
          <span className="font-size-14">
            New {!screenType?.isPortrait && "Project"}
          </span>
        </Button>
      )}
      {accessAllowed(myPermissions, allPermissions.CREATE_PROJECT) && (
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={exportActiveContractExpiryList}
        >
          <span className="font-size-14">
            Export {!screenType?.isPortrait && "Project List"}
          </span>
        </Button>
      )}
      <Button
        shape="circle"
        type="primary"
        icon={<BiFilterAlt />}
        onClick={() => setFilterVisible(!filterVisible)}
        title={filterVisible ? "Hide filters" : "Show filters"}
      />
    </Space>
  );

  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap gutter={16}>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <ProjectOutlined />
              </div>
              <h1 className="app-page-title">Manage Project</h1>
            </div>
            <p className="app-page-subtitle">
              View and manage projects and assignments
            </p>
          </Col>
          <Col>{headerButtons}</Col>
          <Col><span className="font-size-16 font-weight-bold">Enable Total Cost : </span>
            <Switch
              checked={enableCostCalculation}
              onChange={(checked) => setEnableCostCalculation(checked)}
            /></Col>
        </Row>
      </div>
      {filterVisible && (
        <div className="app-filter-bar">
          <div className="app-filter-header">
            <div className="app-filter-title">
              <BiFilterAlt style={{ fontSize: 14 }} />
              Filters
            </div>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setFilterVisible(false)}
              title="Close filters"
              aria-label="Close filters"
            />
          </div>
          <div className="table-filter-form">
            <GenericFormComponent {...formProps} />
          </div>
        </div>
      )}

      <ActiveFilterChipsBar
        show={!filterVisible}
        entries={activeFilterEntries}
        onRemove={removeFilter}
        onClearAll={clearAllFilters}
      />

      {/* <Row
        style={{
          marginBottom: 16,
          backgroundColor: "var(--bg-surface)",
          padding: "12px 20px",
          borderRadius: "var(--radius-lg)",
          border: "1px solid var(--border)",
          boxShadow: "var(--shadow-sm)",
        }}
      >
        <Col span={24} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
          <Space size={8} wrap={false}>
            <span className="font-size-16 font-weight-bold">Enable Total Cost : </span>
            <Switch
              checked={enableCostCalculation}
              onChange={(checked) => setEnableCostCalculation(checked)}
            />
          </Space>
        </Col>
      </Row> */}
      <div className="app-table-card">
        <DataTableComponent
        pagination={true}
        loading={loadingProject || loadingCostCalculation}
        dataSource={projectListData}
        columns={columns}
        onChange={onChangeHandler}
        defaultPage={0}
        currentPage={pagination.page}
        totalRows={pagination?.total}
        onPageChange={onPageChange}
        tableWidth={"1800px"}
      />
      </div>
      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={onDeleteConfirm}
        onCancel={onDeleteCancel}
      >
        <p>Are you sure you want to delete this project ?</p>
      </Modal>
      {createEditOpen && (
        <CreateEditProjectComponent
          onClose={() => setCreateEditOpen(false)}
          visible={createEditOpen}
          selectedEntity={selectedEntity}
          onSubmit={createEditSubmit}
          currencies={currencies}
          projectTypes={projectTypes}
          entityList={entityList}
        />
      )}

      {openLogs && (
        <Drawer open={openLogs} onClose={onCloseLogs} title="Log Details">
          <ActivityLogComponent
            id={selectedProjectId}
            record={selectedEntity}
            module="Project"
          />
        </Drawer>
      )}
      {openStaffAssignment && (
        <StaffAssignmentComponent
          visible={openStaffAssignment}
          onClose={onCloseStaffAssignment}
          selectedEntity={selectedEntity}
        />
      )}
      {openMilestones && selectedEntity && (
        <MilestonesDrawerComponent
          visible={openMilestones}
          onClose={onCloseMilestones}
          selectedEntity={selectedEntity}
        />
      )}
      {bulkImportOpen && (
        <ProjectBulkUploadComponent
          onClose={() => setBulkImportOpen(false)}
          visible={bulkImportOpen}
          selectedEntity={selectedEntity}
          onSubmit={createEditSubmit}
        />
      )}
    </div>
  );
};
export default ManageProjectPage;
